import { useState, useEffect } from 'react'
import { useScaleAnimation } from '../../hooks/useScaleAnimation'
import { GROUPED_STATIC_TEXT_ROLE } from './NameValueTableRow'

type ToggleOption = string

type ToggleBoxPropTypes = {
  isToggleEnabled: boolean
  currentToggleOption: ToggleOption
  toggleOptions: ToggleOption[]
  onToggleClick: (toggleOption: ToggleOption) => void
  toggleColor?: string
  value?: string
  caption?: string
  toggleBoxClassName?: string
  titleClassName?: string
  valueClassName?: string
  captionClassName?: string
  toggleClassName?: string
  /** Details_B15: benchmark toggle hint (aria-description, not aria-label). */
  toggleAriaDescription?: string
}

function buildAccessibleLabel(toggleOption: ToggleOption, value?: string, caption?: string): string {
  return [toggleOption, value, caption].filter(Boolean).join(', ')
}

export default function ToggleBox({
  isToggleEnabled,
  currentToggleOption,
  toggleOptions,
  onToggleClick,
  toggleColor,
  value,
  caption,
  toggleBoxClassName,
  titleClassName,
  valueClassName,
  captionClassName,
  toggleClassName,
  toggleAriaDescription = 'Double tap to change benchmark'
}: ToggleBoxPropTypes) {
  const [toggleOption, setToggleOption] = useState<ToggleOption>(toggleOptions?.[0] ?? '')
  const { scaleStyle, triggerAnimation } = useScaleAnimation()

  useEffect(() => {
    if (currentToggleOption) setToggleOption(() => currentToggleOption)
  }, [currentToggleOption])

  const handleToggle = () => {
    if (!isToggleEnabled || toggleOptions?.length <= 1) {
      return
    }
    triggerAnimation()
    const currentIndex = toggleOptions?.indexOf(toggleOption)
    const nextIndex = currentIndex === toggleOptions?.length - 1 ? 0 : currentIndex + 1
    const nextOption = toggleOptions[nextIndex]
    setToggleOption(() => nextOption)
    onToggleClick(nextOption)
  }

  const accessibleLabel = buildAccessibleLabel(toggleOption, value, caption)

  const labelContent = (
    <div aria-hidden='true' className='flex flex-col gap-[2px]' style={{ maxWidth: 'calc(100% - 20px)' }}>
      <div className={`text-13 font-[500] truncate ${titleClassName ?? ''}`}>{toggleOption}</div>
      {value && <div className={`text-[16px] font-[500]  ${valueClassName ?? ''}`}>{value}</div>}
      {caption && <div className={`text-13 font-[500] text-[#6A6E6E] ${captionClassName ?? ''}`}>{caption}</div>}
    </div>
  )

  const toggleIcon = isToggleEnabled ? (
    <div aria-hidden='true' className={`${toggleClassName ?? ''}`}>
      <svg xmlns='http://www.w3.org/2000/svg' width='15' height='15' viewBox='0 0 15 15' fill='none'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M7.67443 3.16934C7.44866 2.94355 7.08262 2.94355 6.85685 3.16934L4.54435 5.48183C4.37901 5.64718 4.32954 5.89583 4.41902 6.11186C4.50851 6.3279 4.71931 6.46875 4.95314 6.46875H9.57814C9.81197 6.46875 10.0228 6.3279 10.1123 6.11186C10.2017 5.89583 10.1523 5.64718 9.98693 5.48183L7.67443 3.16934ZM4.41902 9.13814C4.50851 8.92211 4.71931 8.78125 4.95314 8.78125H9.57814C9.81197 8.78125 10.0228 8.92211 10.1123 9.13814C10.2017 9.35416 10.1523 9.60282 9.98693 9.76817L7.67443 12.0807C7.44866 12.3064 7.08262 12.3064 6.85685 12.0807L4.54435 9.76817C4.37901 9.60282 4.32954 9.35416 4.41902 9.13814Z'
          fill={`${toggleColor ?? 'black'}`}
        />
      </svg>
    </div>
  ) : null

  if (isToggleEnabled) {
    return (
      <button
        type='button'
        onClick={handleToggle}
        aria-label={accessibleLabel}
        aria-description={toggleAriaDescription}
        className={`w-full min-h-10 flex justify-between rounded-xl py-2 pl-3 pr-[9px] items-center border-none text-left cursor-pointer ${toggleBoxClassName ?? ''}`}
        style={scaleStyle}
      >
        {labelContent}
        {toggleIcon}
      </button>
    )
  }

  return (
    <div
      role={GROUPED_STATIC_TEXT_ROLE}
      aria-label={accessibleLabel}
      className={`w-full min-h-10 flex justify-between rounded-xl py-2 pl-3 pr-[9px] items-center ${toggleBoxClassName ?? ''}`}
      style={scaleStyle}
    >
      {labelContent}
    </div>
  )
}
